<template>
  <div>
    <template v-if="loading">
      <v-progress-circular indeterminate ></v-progress-circular>
    </template>
    <template v-else>
      <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle>{{ item.identityNo }}</v-list-item-subtitle>
            <v-list-item-title>모뎀번호</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

      <div ref="mapContainer" style="height: 200px"></div>

      <v-simple-table v-if="$vuetify.breakpoint.xs" dense>
      <template v-slot:default>
        <tbody>
          <template v-for="({ label, value, filter, visible, suffix }) in parsed">
            <tr :key="label" v-if="visible">
              <td>{{ label }}</td>
              <td>{{ filter(value) }}<span v-if="suffix" class="ml-1">{{ suffix }}</span></td>
            </tr>
          </template>
        </tbody>
      </template>

      </v-simple-table>

      <template v-else>
        <v-container fluid>
          <v-row>
            <template v-for="({ label, value, filter, visible, suffix }, index) in parsed">
              <v-col cols="auto" :key="label" v-if="visible">
                <v-card @click="select(index)">
                  <v-card-title class="pa-2"><h6>{{ label }}</h6></v-card-title>
                  <template>
                    <v-card-text class="pb-2 px-2">
                      {{ filter(value) }}
                      <span v-if="suffix" class="ml-1">{{ suffix }}</span>
                    </v-card-text>
                  </template>
                </v-card>
              </v-col>
            </template>
          </v-row>
        </v-container>

        <v-subheader>Hex ({{ totalLength }} bytes)</v-subheader>

        <v-container fluid>
          <div>
            <span v-for="(hex, index) in hexChunks" :key="index" :class="['mr-1 px-1', `hexblock-${index % 6 + 1}`, selectedClass(index, selected)]">{{ hex }}</span>
          </div>
        </v-container>
      </template>

    </template>
  </div>
</template>

<script>
import { getLastPacket } from '@/api'
import moment from 'moment'
import { Loader } from '@googlemaps/js-api-loader'

const loader = new Loader({
  apiKey: 'AIzaSyBSNxqCl5Fcbe9ehF9KIDj5ypNQQuh6DF4'
})

class PacketParser {
  constructor (
    label,
    length,
    options = {}
  ) {
    this.label = label
    this.unsigned = options.unsigned || false
    this.visible = options.visible === null || options.visible === undefined ? true : options.visible
    this.filter = options.filter ? options.filter : val => val
    this.suffix = options.suffix || ''
    this.length = length
  }

  method (offset) {
    switch (this.length) {
      case 1:
        return (view) => this.unsigned ? view.getUint8(offset) : view.getInt8(offset)
      case 2:
        return (view) => this.unsigned ? view.getUint16(offset, true) : view.getInt16(offset, true)
      case 4:
        return (view) => this.unsigned ? view.getUint32(offset, true) : view.getInt32(offset, true)
      default:
        return (view) => new DataView(view.buffer.slice(offset, offset + this.length))
    }
  }
}

// function toHexString (arr) {
//   return arr.map((int) => int.toString(16).padStart(2, '0').toUpperCase()).join(' ')
// }

const parsers = [
  new PacketParser('Time', 4, { unsigned: true, filter: (timestamp) => moment(new Date(timestamp * 1000)).add(-9, 'hour').format('YYYY-MM-DD HH:mm:ss') }),
  new PacketParser('FW_Ver', 2, { visible: false }),
  new PacketParser('User', 1, { visible: false }),
  new PacketParser('CSQ', 1, { visible: false }),
  new PacketParser('Comm', 1, { visible: false }),
  new PacketParser('OBD_KeyOn', 1, { visible: false }),
  ...new Array(4).fill(0).map((val, index) => {
    return new PacketParser('T[' + index + ']', 2, {})
  }),
  new PacketParser('P', 2, { unsigned: true }),
  new PacketParser('Rate', 2, { visible: false }),
  new PacketParser('BV', 2, { visible: false }),
  new PacketParser('IHC', 2, { visible: false }),
  new PacketParser('f_REG', 2, { visible: false }),
  new PacketParser('CarType', 1, { visible: false }),
  new PacketParser('FPD', 1, { unsigned: true }),
  new PacketParser('Error', 1, { visible: false }),
  new PacketParser('Check', 1, { visible: false }),
  new PacketParser('t_REG', 2, { visible: false }),
  new PacketParser('n_REG', 2, { visible: false }),
  new PacketParser('Sig', 1, { visible: false }),
  new PacketParser('Sts', 1, { visible: false }),
  new PacketParser('Ver', 2, { visible: false }),
  new PacketParser('RegenStartKey', 1, { visible: false }),
  new PacketParser('Lock', 1, { visible: false }),
  new PacketParser('DrvTime', 4, { unsigned: true, filter: (timestamp) => moment(new Date(timestamp * 1000)).add(-9, 'hour').format('YYYY-MM-DD HH:mm:ss') }),
  new PacketParser('baseP1', 2, { visible: false }),
  new PacketParser('AvgT34', 2, { visible: false }),
  new PacketParser('AvgT12', 2, { visible: false }),
  // new PacketParser('Reserved', 48, { filter: (value) => value.byteLength + ' bytes' }),

  new PacketParser('Lat', 4, { unsigned: true, filter: (lat) => (lat * 0.000001).toFixed(6) }),
  new PacketParser('Lon', 4, { unsigned: true, filter: (lon) => (lon * 0.000001).toFixed(6) }),
  new PacketParser('Azi', 2, { visible: false }),
  new PacketParser('Speed', 1, { visible: false }),

  new PacketParser('PurgeValve', 1, { visible: false }),
  new PacketParser('AirValve', 1, { visible: false }),
  new PacketParser('UreaValueRatio', 1, { visible: false }),
  new PacketParser('UreaPress', 1, { visible: false }),
  new PacketParser('AirPress', 1, { visible: false }),
  new PacketParser('PumpPress', 1, { visible: false }),
  new PacketParser('Reserved', 1, { visible: false }),

  new PacketParser('ScrTempUp', 2, { unsigned: true }),
  new PacketParser('ScrTempDown', 2, { unsigned: true }),
  new PacketParser('ScrNOxUp', 2, { unsigned: true }),
  new PacketParser('ScrNOxDown', 2, { unsigned: true }),
  new PacketParser('UreaLevel', 2, { visible: false }),
  new PacketParser('UreaTemp', 2, { visible: false }),
  new PacketParser('AirTemp', 2, { visible: false }),
  new PacketParser('UreaRate', 2, { unsigned: true }),
  new PacketParser('RPM', 2, { visible: false }),
  new PacketParser('Reserved', 40, { filter: (view) => view.byteLength + ' bytes', visible: false })
]

const totalLength = parsers.reduce((sum, parser) => sum + parser.length, 0)

// 67 CA 20 02      A9 49 B3 07

console.log(totalLength)
export default {
  created () {
    this.fetch().finally(() => {
      this.loading = false
      this.interval = setInterval(this.fetch, 1000 * 60)
      this.$nextTick(async () => {
        await this.initMap()
        this.setCenter()
      })
    })
  },
  destroyed () {
    clearTimeout(this.interval)
  },
  data () {
    return {
      identityNo: '1229874051',
      totalLength,
      interval: null,
      item: null,
      loading: true,
      selected: -1,
      map: null,
      google: null,
      marker: null
    }
  },
  watch: {
    position () {
      this.setCenter()
    }
  },
  computed: {
    hexChunks () {
      if (!this.item) return []

      let offset = 0
      return parsers.map(({ length }) => {
        const r = this.item.packet.data.slice(offset, offset += length).map(int => int.toString(16).padStart(2, '0').toUpperCase()).join(' ')

        return r
      })
    },
    parsed () {
      if (!this.item) return null

      const view = new DataView(new ArrayBuffer(128))

      this.item.packet.data.forEach((byte, index) => {
        view.setInt8(index, byte)
      })

      console.log(view)

      let offset = 0

      const r = parsers.map((parser) => {
        const value = parser.method(offset)(view)
        offset += parser.length

        return {
          label: parser.label,
          value,
          filter: parser.filter,
          visible: parser.visible,
          suffix: parser.suffix
        }
      })

      return r
    },
    position () {
      if (!this.parsed) return { latitude: 0, longitude: 0 }
      const lat = this.parsed.find((item) => item.label === 'Lat')
      const lon = this.parsed.find((item) => item.label === 'Lon')

      const latitude = lat ? lat.value : 0.0
      const longitude = lon ? lon.value : 0.0

      return {
        latitude: latitude * 0.000001,
        longitude: longitude * 0.000001
      }
    }
  },
  methods: {
    setCenter () {
      if (this.map && this.google) {
        if (this.marker) this.marker.setMap(null)

        const latLng = {
          lat: this.position.latitude, lng: this.position.longitude
        }

        this.marker = new this.google.maps.Marker({
          position: latLng,
          map: this.map
        })

        this.map.setCenter(latLng)
      }
    },
    async initMap () {
      const mapContainer = this.$refs.mapContainer

      if (mapContainer) {
        const google = await loader.load()

        this.map = new google.maps.Map(mapContainer, {
          disableDefaultUI: true,
          zoom: 14,
          center: {
            lat: 37.393326,
            lng: 126.970475
          }
        })
        this.google = google
      }
    },
    async fetch () {
      const { item } = await getLastPacket({ identityNo: this.identityNo })

      console.log(item)

      this.item = item
    },
    select (index) {
      if (this.selected === index) this.selected = -1
      else this.selected = index
    },
    selectedClass (index, selected) {
      if (selected < 0) return null
      return index === selected ? 'selected' : 'none'
    }
  }
}
</script>

<style scoped>
.hexblock-1 {
  background-color: #FFCDD2
}

.hexblock-2 {
  background-color: #E1BEE7
}

.hexblock-3 {
  background-color: #BBDEFB
}

.hexblock-4 {
  background-color: #B2DFDB
}

.hexblock-5 {
  background-color: #F0F4C3
}

.hexblock-6 {
  background-color: #FFE0B2
}

.none {
  background-color: #FFFFFF;
}
</style>
